// Legacy jQuery/Bootstrap/Flickity/Leaflet - must load first for global $
import "../legacy";

import Turbolinks from "turbolinks";
import { createApp, h } from "vue";
import axios from "axios";
import { createEventBus } from "../event-bus";

axios.interceptors.request.use((config) => {
  config.headers["Authorization"] = "Bearer " + localStorage.getItem("token");
  config.headers["Accept"] = "application/json";
  return config;
});

Turbolinks.start();

function createDestroyVueHandler(app) {
  return function destroyVue() {
    app.unmount();
    document.removeEventListener("turbolinks:before-render", destroyVue);
  };
}

// Convert kebab-case to camelCase (e.g. api-key -> apiKey) for Vue prop names
function kebabToCamel(str) {
  return str.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
}

// Helper function to manually instantiate Vue components from HTML tags
function instantiateComponents(rootElement, componentMap) {
  const componentInstances = [];
  const mountPromises = [];

  Object.keys(componentMap).forEach((tagName) => {
    const elements = rootElement.querySelectorAll(tagName);
    elements.forEach((el) => {
      if (el.__vue__) return;

      const propsData = {};
      Array.from(el.attributes).forEach((attr) => {
        let propName = attr.name;
        let propValue = attr.value;

        if (propName.startsWith(":")) {
          propName = propName.substring(1);
        } else if (propName.startsWith("v-bind:")) {
          propName = propName.substring(7);
        }
        propName = kebabToCamel(propName);

        if (propValue) {
          try {
            propValue = JSON.parse(propValue);
          } catch (e) {
            if (propValue === "true") propValue = true;
            else if (propValue === "false") propValue = false;
            else if (propValue === "null") propValue = null;
            else if (!isNaN(propValue) && propValue !== "")
              propValue = Number(propValue);
          }
        }

        propsData[propName] = propValue;
      });

      const mountPromise = componentMap[tagName]()
        .then((component) => {
          const Component = component.default || component;
          // Vue 3: use createApp instead of new Component() + $mount()
          const app = createApp(Component, propsData);
          app.config.globalProperties.$http = axios;
          app.config.globalProperties.$eventBus = createEventBus();
          app.mount(el);

          componentInstances.push(app);
          return app;
        })
        .catch((err) => {
          console.error(`Failed to load component ${tagName}:`, err);
        });
      mountPromises.push(mountPromise);
    });
  });

  return {
    instances: componentInstances,
    allMounted: Promise.all(mountPromises),
  };
}

// Process data-bg-url attributes and set background images (CSP-compliant)
// Used by Listing component in listing-slider on show page
// Exposed globally so listing-map can call when Leaflet popup opens (popup content is injected after initial mount)
function processDataBgUrls() {
  const elementsWithBg = document.querySelectorAll(
    "[data-bg-url]:not([data-bg-processed])",
  );
  if (elementsWithBg.length === 0) return;

  const nonceMeta = document.querySelector('meta[name="csp-nonce"]');
  const nonce = nonceMeta ? nonceMeta.getAttribute("content") : null;

  let styleSheet = document.getElementById("dynamic-bg-styles");
  if (!styleSheet) {
    styleSheet = document.createElement("style");
    styleSheet.id = "dynamic-bg-styles";
    if (nonce) styleSheet.setAttribute("nonce", nonce);
    document.head.appendChild(styleSheet);
  }
  const sheet = styleSheet.sheet;

  elementsWithBg.forEach((element, index) => {
    const bgUrl = element.getAttribute("data-bg-url");
    if (bgUrl && !element.hasAttribute("data-bg-processed")) {
      element.setAttribute("data-bg-processed", "true");
      let uniqueId = element.id;
      if (!uniqueId) {
        uniqueId = `bg-dynamic-${index}-${Math.random()
          .toString(36)
          .substr(2, 9)}`;
        element.id = uniqueId;
      }
      const escapedUrl = bgUrl
        .replace(/'/g, "\\'")
        .replace(/"/g, '\\"')
        .replace(/\\/g, "\\\\");
      try {
        sheet.insertRule(
          `#${uniqueId} { background-image: url("${escapedUrl}") !important; }`,
          sheet.cssRules.length,
        );
      } catch (e) {
        console.warn("Could not inject CSS rule:", e);
      }
    }
  });
}
window.processDataBgUrls = processDataBgUrls;

const HUBSPOT_EMBED_SCRIPT = "https://js.hsforms.net/forms/embed/46222981.js";

/** Contact page: .hs-form-frame lives inside #app; Vue reapplies innerHTML so inline <script> tags never run — load embed after restore. */
function ensureContactPageHubSpotEmbed() {
  const root = document.getElementById("contact-hubspot-root");
  if (!root) return;
  const frame = root.querySelector(".hs-form-frame");
  if (!frame || frame.querySelector(".hs-form, iframe")) return;
  const script = document.createElement("script");
  script.src = `${HUBSPOT_EMBED_SCRIPT}?_=${Date.now()}`;
  script.defer = true;
  document.head.appendChild(script);
}

function setupContactHubspotReadyUI() {
  const root = document.getElementById("contact-hubspot-root");
  if (!root) return;
  const frame = root.querySelector(".hs-form-frame");
  const loading = root.querySelector("[data-hubspot-loading]");
  function ready() {
    root.classList.add("contact__form-wrapper--hubspot-ready");
    root.setAttribute("aria-busy", "false");
    if (loading) loading.setAttribute("aria-hidden", "true");
  }
  if (!frame) return;
  if (frame.querySelector("iframe")) {
    ready();
    return;
  }
  const obs = new MutationObserver(() => {
    if (frame.querySelector("iframe")) {
      obs.disconnect();
      ready();
    }
  });
  obs.observe(frame, { childList: true, subtree: true });
}

document.addEventListener("turbolinks:load", () => {
  const element = document.getElementById("app");
  if (!element) return;

  const originalHTML = element.innerHTML;
  const originalOuterHTML = element.outerHTML;

  const dataAttributes = {};
  Array.from(element.attributes).forEach((attr) => {
    if (attr.name.startsWith("data-")) {
      dataAttributes[attr.name] = attr.value;
    }
  });

  // Component registry for manual instantiation (paths relative to entrypoints/)
  const componentRegistry = {
    "hero-slider": () => import("../components/hero-slider.vue"),
    listing: () => import("../components/listing.vue"),
    "listing-map": () => import("../components/listing-map.vue"),
    "listing-approximate-map": () =>
      import("../components/listing-approximate-map.vue"),
    "listing-index": () => import("../components/listing-index.vue"),
    "listing-search": () => import("../components/listing-search.vue"),
    "listing-show-map": () => import("../components/listing-show-map.vue"),
    "listing-slider": () => import("../components/listing-slider.vue"),
    "gallery-modal": () => import("../components/gallery-modal.vue"),
    "secondary-reviews-slider": () =>
      import("../components/secondary-reviews-slider.vue"),
    "primary-reviews-slider": () =>
      import("../components/primary-reviews-slider.vue"),
    "listing-toggle-description": () =>
      import("../components/listing-toggle-description.vue"),
    "service-card": () => import("../components/service-card.vue"),
    "home-search-v2": () => import("../components/home-search-v2.vue"),
    "custom-select": () => import("../components/custom-select.vue"),
    "admin-listing-address": () =>
      import("../components/admin/admin-listing-address.vue"),
    "admin-neighborhood-new": () =>
      import("../components/admin/admin-neighborhood-new.vue"),
    "dashboard-requests": () => import("../components/dashboard-requests.vue"),
    "copy-text": () => import("../components/copy-text.vue"),
    tooltip: () => import("../components/tooltip.vue"),
    "icon-tooltip": () => import("../components/icon-tooltip.vue"),
    "listings-for-export": () =>
      import("../components/listings-for-export.vue"),
    "email-export": () => import("../components/email-export.vue"),
    home: () => import("../components/home.vue"),
    "new-homepage": () => import("../components/home.vue"),
    "explore-services": () => import("../components/explore-services.vue"),
    "explore-spaces": () => import("../components/explore-spaces.vue"),
    "hubspot-form": () => import("../components/hubspot-form.vue"),
    "list-space-modal": () => import("../components/list-space-modal.vue"),
    "pro-contact-modal": () => import("../components/pro-contact-modal.vue"),
    "book-modal": () => import("../components/book-modal.vue"),
  };

  const app = createApp({
    render: () => h("div", { id: "app" }),

    beforeMount: function() {
      this.$originalElement = originalOuterHTML;
      this.$originalHTML = originalHTML;
      this.$componentInstances = [];
    },

    mounted: function() {
      if (this.$el && dataAttributes) {
        Object.keys(dataAttributes).forEach((attrName) => {
          this.$el.setAttribute(attrName, dataAttributes[attrName]);
        });
      }

      // Restore the original HTML content immediately so the page structure loads
      if (this.$el && this.$originalHTML) {
        this.$el.innerHTML = this.$originalHTML;

        // Manually instantiate Vue components from component tags in the HTML
        this.$nextTick(() => {
          ensureContactPageHubSpotEmbed();
          setupContactHubspotReadyUI();
          const { instances, allMounted } = instantiateComponents(
            this.$el,
            componentRegistry,
          );
          this.$componentInstances = instances;
          // Process data-bg-url after all async components have mounted (fixes listing-slider images on first load)
          allMounted.then(() => {
            this.$nextTick(processDataBgUrls);
          });
        });
      }
      $(document).trigger("load");
    },

    unmounted: function() {
      // Destroy all manually instantiated component instances
      if (this.$componentInstances) {
        this.$componentInstances.forEach((instance) => {
          if (instance && typeof instance.unmount === "function") {
            instance.unmount();
          }
        });
      }

      if (this.$originalElement && element.parentNode) {
        element.outerHTML = this.$originalElement;
      }
    },

    created: function() {},
    methods: {},
    components: {
      "hero-slider": () => import("../components/hero-slider.vue"),
      listing: () => import("../components/listing.vue"),
      "listing-map": () => import("../components/listing-map.vue"),
      "listing-approximate-map": () =>
        import("../components/listing-approximate-map.vue"),
      "listing-index": () => import("../components/listing-index.vue"),
      "listing-search": () => import("../components/listing-search.vue"),
      "listing-show-map": () => import("../components/listing-show-map.vue"),
      "listing-slider": () => import("../components/listing-slider.vue"),
      "secondary-reviews-slider": () =>
        import("../components/secondary-reviews-slider.vue"),
      "primary-reviews-slider": () =>
        import("../components/primary-reviews-slider.vue"),
      "listing-toggle-description": () =>
        import("../components/listing-toggle-description.vue"),
      "service-card": () => import("../components/service-card.vue"),
      "home-search": () => import("../components/home-search.vue"),
      "home-search-v2": () => import("../components/home-search-v2.vue"),
      "custom-select": () => import("../components/custom-select.vue"),
      "admin-listing-address": () =>
        import("../components/admin/admin-listing-address.vue"),
      "admin-neighborhood-new": () =>
        import("../components/admin/admin-neighborhood-new.vue"),
      "dashboard-requests": () =>
        import("../components/dashboard-requests.vue"),
      "copy-text": () => import("../components/copy-text.vue"),
      tooltip: () => import("../components/tooltip.vue"),
      "icon-tooltip": () => import("../components/icon-tooltip.vue"),
      "listings-for-export": () =>
        import("../components/listings-for-export.vue"),
      "email-export": () => import("../components/email-export.vue"),
      home: () => import("../components/home.vue"),
      "new-homepage": () => import("../components/home.vue"),
      "explore-services": () => import("../components/explore-services.vue"),
      "explore-spaces": () => import("../components/explore-spaces.vue"),
      "list-space-modal": () => import("../components/list-space-modal.vue"),
      "pro-contact-modal": () => import("../components/pro-contact-modal.vue"),
      "book-modal": () => import("../components/book-modal.vue"),
    },
  });
  app.config.productionTip = false;
  app.config.globalProperties.$http = axios;
  app.config.globalProperties.$eventBus = createEventBus();
  app.mount(element);

  const destroyVue = createDestroyVueHandler(app);
  document.addEventListener("turbolinks:before-render", destroyVue);
});
